<!--
  Infobulle d'aide, employee dans TOUT le projet (26 composants).

  DS-1.18 — CE COMPOSANT A EVOLUE, ET `.zi-tip` DU PROTOTYPE N'A PAS ETE
  INTRODUITE A COTE. `ds-1-2:742` l'interdisait et avait raison sur ce point :
  deux mecanismes d'infobulle coexistants seraient pires que l'ecart. Le
  comportement du prototype (`components.css:304-316` et `app.js:267-269`) est
  donc TRANSPOSE ici.

  Ce qui manquait, releve en recette le 2026-10-23 :
  - le declencheur etait un `<span>` : INATTEIGNABLE AU CLAVIER. C'est
    maintenant un `<button type="button">`, et l'infobulle s'ouvre au focus.
  - aucun `role="tooltip"` ni `aria-describedby` : un lecteur d'ecran ne
    rattachait pas la bulle au champ.
  - aucune fermeture par Echap.
  - le survol ne portait que sur l'ICONE, pas sur le conteneur : viser 16 px
    demandait de la precision.
  - la taille de l'icone n'etait pas parametrable, alors que le prototype
    emploie 14 px dans un libelle de champ et 16 px dans un titre de carte.
  - aucune mise en evidence du champ concerne.

  ⚠ LE `Teleport` VERS `<body>` EST CONSERVE, volontairement. Le prototype se
  contente de CSS pur (`position: absolute` dans un parent `relative`), ce qui
  suffit a une page statique mais pas ici : `CLAUDE.md` documente le vrai
  probleme que le Teleport resout — une bulle en `absolute` est coupee par
  l'`overflow` d'une modale. Ne pas revenir au CSS pur.

  🚨 L'ICONE EST UN « i » CERCLE — ARBITRAGE DE L'UTILISATEUR DU 2026-10-25
  (DS-1.23, AC5), QUI REVIENT SUR L'ARBITRAGE 8 DU 2026-10-23.
  Demande, mot pour mot : « pour les tooltip c'est une sorte de i dans un rond
  dans les maquettes, pas un point d'interrogation dans un rond, fait pareil. »
  Le prototype disait deja « i » — `components.css:304` litteralement :
  « bouton "i" accessible au clavier » — et c'est desormais SUIVI. L'icone est
  `IconInfo`, deja au sprite : un cercle en `currentColor` avec la barre et le
  point du « i ».
  ⛔ NE PAS REVENIR AU « ? » (`IconHelp`). DS-1.18 avait fait l'inverse a la
  demande de l'utilisateur, qui a tranche depuis. `IconHelp` reste au sprite,
  il peut servir ailleurs.
  ⚠ Ce composant sert 26 ecrans : tout changement ici porte sur toute
  l'application, et se recette hors du module ACV (AC15).
-->
<template>
  <span
    class="tooltip-wrapper"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
  >
    <span class="tooltip-container" :class="titleClass">
      {{ title }}
      <!-- Le declencheur est un `<button>` et non un `<span>` : c'est ce qui le
           rend atteignable au clavier. `type="button"` est indispensable — dans
           un formulaire, un bouton sans type soumet. -->
      <button
        ref="triggerRef"
        type="button"
        class="tooltip-trigger"
        :aria-label="ariaLabel || $t('general.help')"
        :aria-describedby="show ? tooltipId : null"
        @focus="showTooltip"
        @blur="hideTooltip"
        @click.prevent
      >
        <zi-icon name="IconInfo" :size="size" />
      </button>
    </span>

    <!-- Teleport tooltip to body to escape modal overflow -->
    <Teleport to="body">
      <div
        v-if="show"
        :id="tooltipId"
        ref="tooltip"
        role="tooltip"
        class="tooltip tooltip-teleported"
        v-html="innerHtml"
        :class="{
          'centered-tooltip': centered,
          'centered-left-tooltip': centeredLeft
        }"
        :style="tooltipStyle"
        @mouseenter="show = true"
        @mouseleave="hideTooltip"
      >
      </div>
    </Teleport>
  </span>
</template>
<script>
/* Compteur de module : il ne sert qu'a donner a chaque bulle un `id` unique,
   pour qu'`aria-describedby` pointe sur la bonne. Plusieurs infobulles
   cohabitent dans une meme carte. */
let tooltipSeq = 0

import ziIcon from './ziIcon.vue'

export default {
  components: {
    ziIcon
  },
  props: {
    title: String,
    titleClass: String,
    innerHtml: String,
    centered: Boolean,
    centeredLeft: Boolean,
    /**
     * Cote de l'icone, en pixels. DS-1.18 : le prototype emploie 14 dans un
     * libelle de champ et 16 dans un titre de carte. 16 par defaut, qui est la
     * taille de l'echelle du design system (`icon-s`) et celle des 26 emplois
     * existants, inchanges.
     */
    size: {
      type: [Number, String],
      default: 16
    },
    /**
     * Texte du bouton pour un lecteur d'ecran. Repli sur « Aide »
     * (`general.help`, presente dans les 4 locales). A preciser quand la page
     * porte plusieurs infobulles et que « Aide » seul serait ambigu.
     */
    ariaLabel: {
      type: String,
      default: ''
    }
  },
  data() {
    tooltipSeq += 1
    return{
      isLoading: true,
      show: false,
      tooltipStyle: {},
      tooltipId: `tooltip-${tooltipSeq}`
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.adjustTooltipPosition();
        });
      } else {
        this.tooltipStyle = {};
      }
    }
  },
  async mounted() {
    /* Fermeture par Echap. Transpose de `app.js:267-269` du prototype, qui le
       fait en deux lignes sur le document entier. Ici le composant est monte
       plusieurs fois par page : le handler est pose au niveau du document mais
       ne fait quelque chose QUE si cette bulle-ci est ouverte. */
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    showTooltip() {
      this.show = true;
    },
    hideTooltip() {
      this.show = false;
    },
    onKeydown(event) {
      if (event.key !== 'Escape' || !this.show) return;
      this.show = false;
      /* Le focus reste sur le declencheur : sans cela, `@focus` le
         reouvrirait aussitot, et l'utilisateur ne pourrait pas la fermer. On
         le rend donc explicitement au bouton, bulle fermee. */
      if (this.$refs.triggerRef) this.$refs.triggerRef.blur();
    },
    adjustTooltipPosition() {
      const tooltip = this.$refs.tooltip;
      const iconRef = this.$refs.triggerRef;
      if (!tooltip || !iconRef) return;

      // Get icon position in viewport
      const iconRect = iconRef.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      // Initial positioning below the icon
      let top = iconRect.bottom + 8; // 8px spacing below icon
      let left = iconRect.left;

      // Measure tooltip dimensions with temporary positioning
      this.tooltipStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        right: 'auto',
        bottom: 'auto'
      };

      this.$nextTick(() => {
        const tooltipRect = tooltip.getBoundingClientRect();

        // If tooltip goes off right edge, align to right
        if (tooltipRect.right > windowWidth - 16) {
          left = windowWidth - tooltipRect.width - 16; // 16px padding from edge
        }

        // If tooltip goes off left edge, align to left
        if (left < 16) {
          left = 16;
        }

        // If tooltip goes off bottom, position above icon instead
        if (tooltipRect.bottom > windowHeight - 16) {
          top = iconRect.top - tooltipRect.height - 8;
        }

        // If still goes off top, position at top of screen
        if (top < 16) {
          top = 16;
        }

        this.tooltipStyle = {
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          right: 'auto',
          bottom: 'auto'
        };
      });
    }
  },
  created() {

  }
}

</script>

<style scoped>
.tooltip-wrapper {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.tooltip-container {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* Le declencheur. Valeurs du prototype (`.zi-tip__trigger`,
   `components.css:305`) : pas de bordure, pas de fond, un rayon en pastille et
   `cursor: help`. Le padding de 2 px porte la zone cliquable a 20 px pour une
   icone de 16, ce qui reste sous les 24 px recommandes — le bouton est
   volontairement discret a cote d'un libelle, et il est atteignable au clavier,
   ce qui etait le vrai defaut. */
.tooltip-trigger {
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border: 0;
  border-radius: var(--radius-pill);
  background: none;
  color: var(--icon-medium);
  cursor: help;
}
.tooltip-trigger:hover {
  color: var(--icon-enabled);
}
/* Anneau de focus du design system (§6) : 3 px decale de 2 px, et jamais un
   simple changement de couleur. C'est ce qui rend visible la navigation au
   clavier, que ce composant ne permettait meme pas jusqu'ici. */
.tooltip-trigger:focus-visible {
  outline: 3px solid var(--color-primary-accessible);
  outline-offset: 2px;
}

/* Override default tooltip positioning for teleported tooltips */
.tooltip-teleported {
  position: fixed !important;
  z-index: 20000 !important;
  pointer-events: auto;
  width: auto !important;
  max-width: 300px;
}

.tooltip-teleported.centered-tooltip {
  transform: none !important;
}

.tooltip-teleported.centered-left-tooltip {
  transform: none !important;
}
</style>
